import uniqBy from "lodash-es/uniqBy";
import * as React from "react";
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { QueryCondition, QueryFilter } from "../../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { LocalQueryTable } from "../../../misc/components/queryTable/LocalQueryTable";
import MultiSelectModal from "../../../misc/components/queryTable/MultiSelectModal";
import { QueryTableColumn } from "../../../misc/components/queryTable/QueryTable";
import { Plus24hrTimeRangeModal } from "../../../misc/components/queryTable/Plus24hrTimeRangeModal";
import { instanceOfPeaksPoint, PeaksPeriod, PeaksPoint } from "../interfacesPeaks/PeaksJS";
import { formatToHHmmss_sss, HHmmssToTotalSeconds } from "../../../misc/timeFormatting";
import { Query } from '../../../misc/components/queryTable/Query';
import { Checkbox } from "../../../forms/informedv2/Checkbox";
import { faCheck, faShare, faTrash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquare } from "@fortawesome/free-regular-svg-icons";

interface EventListTableProps {
    timestampColumn: "timestamp" | "startTime" | "rawTimestamp";
    startTimeColumnName: "Start Time" | "Waveform Time";
    withEndTime: boolean;
    events: Array<PeaksPoint | PeaksPeriod>;
    renderRawTime?: boolean;
    onJumpToEventClick: (timepoint: number) => void;
    onDeleteEventClick: (id: string) => void;
    onConfirmDuplicateThresholdEventClick?: (id: string) => void;
    query: Query<any>;
    selectedSegment: number;
}

export const EventListTable = (props: EventListTableProps) => {
    const childRef = React.useRef();
    const [showEventTypeFilter, setShowEventTypeFilter] = React.useState<boolean>(false);
    const [showStartTimeFilter, setShowStartTimeFilter] = React.useState<boolean>(false);
    const [showRawTimeFilter, setShowRawTimeFilter] = React.useState<boolean>(false);
    const [filters, setFilters] = React.useState(props.query ? props.query.Filters : []);
    const [multiDayOffset, setMultiDayOffset] = React.useState<number>();
    const [eventTypeTempValues, setEventTypeTempValues] = React.useState<string[]>([]);

    React.useEffect(() => {
        const getfilteredSegments = () => {
            const segmentHoursValue = (props.selectedSegment - 1) * 24
            const segmentSecsStartTime = segmentHoursValue * 60 * 60;
            setMultiDayOffset(segmentSecsStartTime);
        }

        getfilteredSegments();

    }, [props.selectedSegment]);

    const renderLabel = (event: PeaksPoint | PeaksPeriod) => {
        const tooltip = <Tooltip id="DuplicateThresholdWarningTooltip" style={{ zIndex: 2500 }}>
            Event is within the duplicate threshold
        </Tooltip>

        return (
            <>
                {event.withinDuplicateThreshold && !event.confirmedValidDuplicate ? <><OverlayTrigger placement="top" overlay={tooltip}><FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" /></OverlayTrigger> &nbsp;</> : null}
                <FontAwesomeIcon icon={faSquare} style={{ color: event.color }} />&nbsp;
                {event.eventTypeName}
            </>
        );
    };

    const renderHamburgerMenu = (event: PeaksPoint & PeaksPeriod) => {
        return (
            <>
                <button
                    className="btn btn-primary btn-xs"
                    onClick={() =>
                        props.onJumpToEventClick(
                            event.isPoint ? event.timestamp : event.startTime
                        )
                    }
                >
                    <FontAwesomeIcon icon={faShare} />
                </button>
                &nbsp;
                <button
                    className="btn btn-danger btn-xs"
                    disabled={!event.editable}
                    onClick={() => props.onDeleteEventClick(event.id)}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
                &nbsp;
                {event.withinDuplicateThreshold && !event.confirmedValidDuplicate ? <button
                    className="btn btn-warning btn-xs"
                    disabled={!event.editable}
                    onClick={() => props.onConfirmDuplicateThresholdEventClick(event.id)}
                >
                    <FontAwesomeIcon icon={faCheck} />
                </button> : null}
            </>
        );
    };

    const handleFilter = (member: keyof (PeaksPoint & PeaksPeriod)) => {
        switch (member) {
            case "eventTypeName":
                const currentValues = filters.find(x => x.Member === "eventTypeName")?.Values || [];
                setEventTypeTempValues(currentValues.map(String));
                setShowEventTypeFilter(true);
                break;
            case "timestamp":
            case "startTime":
                setShowStartTimeFilter(true);
                break;
            case "rawTimestamp":
                setShowRawTimeFilter(true);
                break;
            default:
                break;
        }
    };

    const handleFilterClose = () => {
        setShowEventTypeFilter(false);
        setShowStartTimeFilter(false);
        setShowRawTimeFilter(false);
    };

    const handleMultiSelectFilterApply = (
        member: keyof (PeaksPoint | PeaksPeriod),
        values: string[]
    ) => {
        let eventTypeNameFilter = filters.find(x => x.Member === "eventTypeName");
        if (eventTypeNameFilter) {
            if (values.length > 0) {
                eventTypeNameFilter.Values = values;
            } else {
                eventTypeNameFilter = null;
            }
        } else {
            eventTypeNameFilter = {
                Member: "eventTypeName",
                Condition: QueryCondition.Equal,
                Values: values
            };
        }

        const newFilters = [
            ...filters.filter(x => x.Member !== "eventTypeName"),
            eventTypeNameFilter
        ];

        setFilters(newFilters.filter(x => x).filter(x => !x.Values.includes(undefined)));
    };

    const handleDateRangeFilterChange = (from: string, to: string, member: string) => {
        let fromFilter = filters.find(
            x => x.Member === member && x.Condition === QueryCondition.GreaterThanOrEqual
        );

        if (fromFilter && from) {
            fromFilter.Values = [from];
        } else if (from === null || from === undefined || from === "") {
            fromFilter = null;
        } else {
            fromFilter = {
                Member: member,
                Condition: QueryCondition.GreaterThanOrEqual,
                Values: [from]
            };
        }

        let toFilter = filters.find(
            x => x.Member === member && x.Condition === QueryCondition.LessThanOrEqual
        );

        if (toFilter && to) {
            toFilter.Values = [to];
        } else if (to === null || to === undefined || to === "") {
            toFilter = null;
        } else {
            toFilter = {
                Member: member,
                Condition: QueryCondition.LessThanOrEqual,
                Values: [to]
            };
        }

        const newFilters = [
            ...filters.filter(x => x.Member !== member),
            fromFilter,
            toFilter
        ];

        setFilters(newFilters.filter(x => x).filter(x => !x.Values.includes(undefined)));
    };

    const handleDuplicateEventsFilter = (duplicateHighlighted: boolean) => {
        const member = "withinDuplicateThreshold";

        let newFilters = [];
        if (duplicateHighlighted) {
            newFilters = [
                ...filters.filter(x => x.Member !== member),
                { Member: member, Condition: QueryCondition.Equal, Values: [duplicateHighlighted] }
            ]
        } else {
            newFilters = [...filters.filter(x => x.Member !== member)]
        }

        setFilters(newFilters);
    }

    const handleFilterClear = (member: string) => {
        setFilters(filters.filter(x => x.Member !== member));

        handleFilterClose();
    }

    const filterDatesCallback = (
        event: PeaksPoint | PeaksPeriod,
        totalFromSeconds: number,
        totalToSeconds: number,
        column: string
    ): boolean => {
        let rawOrRelativeTimestamp;
        if (instanceOfPeaksPoint(event) && column === "rawTimestamp") {
            rawOrRelativeTimestamp = event.rawTimestamp;
        } else if (instanceOfPeaksPoint(event) && column === "timestamp") {
            rawOrRelativeTimestamp = event.timestamp;
        } else if (!instanceOfPeaksPoint(event) && column === "startTime")
            rawOrRelativeTimestamp = event.startTime;

        return (
            (totalFromSeconds !== null ? rawOrRelativeTimestamp >= totalFromSeconds : true) &&
            (totalToSeconds !== null ? rawOrRelativeTimestamp <= totalToSeconds : true)
        );
    };

    const filterEventTypes = (
        filterValues: string[]
    ) => {
        let filteredEvents: Array<PeaksPoint | PeaksPeriod>;
        if (filterValues.length > 0) {
            filteredEvents = props.events.filter(x => filterValues.includes(x["eventTypeId"]));
        } else {
            filteredEvents = props.events;
        }
        return filteredEvents;
    };

    const filterDuplicateThreshold = (
        events: (PeaksPoint | PeaksPeriod)[],
        filterValue: QueryFilter[]
    ) => {
        let filteredEvents: Array<PeaksPoint | PeaksPeriod>;
        if (filterValue.length > 0) {
            filteredEvents = events.filter(x => x.withinDuplicateThreshold === filterValue[0].Values[0] && !x.confirmedValidDuplicate);
        } else {
            filteredEvents = events;
        }
        return filteredEvents;
    };

    const renderFilterModals = (eventTypeFilterValues: string[]) => {
        return (
            <>
                <MultiSelectModal
                    header="Event Type Filter"
                    show={showEventTypeFilter}
                    options={getEventTypeOptions()}
                    valueKey="Value"
                    labelKey="Label"
                    values={eventTypeTempValues}
                    onChange={values => setEventTypeTempValues(values.map(v => v.Value))}
                    onApply={() => {
                        handleMultiSelectFilterApply("eventTypeId", eventTypeTempValues);
                        handleFilterClose();
                    }}
                    onClose={handleFilterClose}
                    onClear={() => handleFilterClear("eventTypeName")}
                />
                <Plus24hrTimeRangeModal
                    header="Raw Time Filter"
                    show={showRawTimeFilter}
                    from={filters.find(x => x.Member === "rawTimestamp" && x.Condition === QueryCondition.GreaterThanOrEqual)?.Values[0] as string}
                    to={filters.find(x => x.Member === "rawTimestamp" && x.Condition === QueryCondition.LessThanOrEqual)?.Values[0] as string}
                    onChange={(from, to) =>
                        handleDateRangeFilterChange(from, to, "rawTimestamp")
                    }
                    onClose={handleFilterClose}
                    onClear={() => handleFilterClear("rawTimestamp")}
                />
                <Plus24hrTimeRangeModal
                    header={`${props.startTimeColumnName} Filter`}
                    show={showStartTimeFilter}
                    from={filters.find(x => x.Member === props.timestampColumn && x.Condition === QueryCondition.GreaterThanOrEqual)?.Values[0] as string}
                    to={filters.find(x => x.Member === props.timestampColumn && x.Condition === QueryCondition.LessThanOrEqual)?.Values[0] as string}
                    onChange={(from, to) => handleDateRangeFilterChange(from !== undefined && from !== null && from !== "" ? formatToHHmmss_sss(HHmmssToTotalSeconds(from) - multiDayOffset) : null, to !== undefined && to !== null && to !== "" ? formatToHHmmss_sss(HHmmssToTotalSeconds(to) - multiDayOffset) : null, props.timestampColumn)} // This uses the timestamp value directly from peaks so needs the multi day offset adding in
                    onClose={handleFilterClose}
                    onClear={() => handleFilterClear(props.timestampColumn)}
                />
            </>
        );
    }

    const renderRawTimeColumn = () => {
        return props.renderRawTime ? (
            <QueryTableColumn
                member="rawTimestamp"
                header="Raw Time"
                map={(row: PeaksPoint & PeaksPeriod) =>
                    row.isPoint
                        ? formatToHHmmss_sss(row.rawTimestamp)
                        : formatToHHmmss_sss(row.startTime)
                }
                sortable={true}
                filterable={true}
            />
        ) : null;
    }

    const getFilteredEventsForFilteredDates = (
        filteredEvents: Array<PeaksPoint | PeaksPeriod>,
        startTotalSeconds: number,
        endTotalSeconds: number,
        column: string
    ) =>
        filteredEvents.filter(x => filterDatesCallback(x, startTotalSeconds, endTotalSeconds, column));

    const filterByTime = (filteredEvents: Array<PeaksPoint | PeaksPeriod>, column: string) => {

        const startFilter = filters.find(x => x.Member === column && x.Condition === QueryCondition.GreaterThanOrEqual)?.Values[0] as string;
        const endFilter = filters.find(x => x.Member === column && x.Condition === QueryCondition.LessThanOrEqual)?.Values[0] as string;

        if ((startFilter !== undefined && startFilter !== "") || (endFilter !== undefined && endFilter !== "")) {
            filteredEvents = getFilteredEventsForFilteredDates(
                filteredEvents,
                HHmmssToTotalSeconds(startFilter),
                endFilter !== undefined && endFilter !== null ? HHmmssToTotalSeconds(endFilter) : null,
                column
            );
        }
        return filteredEvents;
    }

    const getFilteredEventsAndFilterValues = () => {
        let filteredEvents: Array<PeaksPoint | PeaksPeriod> = [];

        const eventTypeFilterValues = [
            ...filters
                .filter(x => x.Member === "eventTypeName")
                .map(y => y.Values.map(v => String(v))).flat()
        ];

        filteredEvents = filterEventTypes(eventTypeFilterValues);

        const duplicateFilterValue = filters.filter(x => x.Member === "withinDuplicateThreshold");

        filteredEvents = filterDuplicateThreshold(filteredEvents, duplicateFilterValue);

        return { filteredEvents, eventTypeFilterValues };
    }

    const getEventTypeOptions = () => {
        return uniqBy(props.events, e => e.eventTypeId).map(m => {
            return { Label: m.eventTypeName, Value: m.eventTypeId };
        });
    }

    const renderEndTimeColumn = () => {
        return props.withEndTime ? (
            <QueryTableColumn
                member="endTime"
                header="End Time"
                map={(row: PeaksPoint & PeaksPeriod) =>
                    row.isPoint ? "" : formatToHHmmss_sss(row.endTime + multiDayOffset)
                }
                sortable={true}
                filterable={false}
            />
        ) : null;
    }

    if (props.events.length === 0) {
        return (
            <Alert show={true} category={AlertCategoryEnum.Success}>
                There are no events currently marked
            </Alert>
        );
    }

    let {
        filteredEvents,
        eventTypeFilterValues
    } = getFilteredEventsAndFilterValues();

    if ((filteredEvents as PeaksPoint[]).some(o => o.rawTimestamp !== undefined))
        filteredEvents = filterByTime(filteredEvents, "rawTimestamp");

    filteredEvents = filterByTime(filteredEvents, props.timestampColumn);

    const duplicateFilterValue = filters.filter(x => x.Member === "withinDuplicateThreshold")[0]?.Values[0];

    return (
        <>
            {props.timestampColumn === "timestamp" ? <Checkbox id="ToggleDuplicate" field="ToggleDuplicate" label="Show Duplicates only" onChange={() => handleDuplicateEventsFilter(!duplicateFilterValue as boolean)} /> : null}
            <LocalQueryTable
                collection={filteredEvents}
                defaultPageSize={10}
                onFilter={handleFilter}
                filters={filters}
                ref={childRef}
                query={props.query}
                rowUniqueIdentifier={row => row.id}
            >
                <QueryTableColumn
                    member="eventTypeName"
                    header="Event Type"
                    map={(row: PeaksPoint & PeaksPeriod) => renderLabel(row)}
                    sortable={true}
                    filterable={true}
                />
                {renderRawTimeColumn()}
                <QueryTableColumn
                    member={props.timestampColumn}
                    header={props.startTimeColumnName}
                    map={(row: PeaksPoint & PeaksPeriod) =>
                        row.isPoint
                            ? row.timestamp === 0 && multiDayOffset === 0 ? formatToHHmmss_sss(0) : formatToHHmmss_sss(row.timestamp + multiDayOffset)
                            : row.startTime === 0 && multiDayOffset === 0 ? formatToHHmmss_sss(0) : formatToHHmmss_sss(row.startTime + multiDayOffset)
                    }
                    sortable={true}
                    filterable={true}
                />
                {renderEndTimeColumn()}
                <QueryTableColumn
                    member=""
                    header=""
                    map={row => renderHamburgerMenu(row)}
                />
            </LocalQueryTable>
            {renderFilterModals(eventTypeFilterValues)}
        </>
    );
}
